import { ADMIN_TEMPLATE_THEMES, ADMIN_TEMPLATES } from '@/mocks/adminTemplates';
import { defineStore } from 'pinia';
import { ref, toRaw } from 'vue';
import type { IAdminCategory } from '@/mocks/adminLibrary';
import type { IAdminTemplate, IAdminTemplateField, IAdminTemplateTheme } from '@/mocks/adminTemplates';

// Back-office mock-up: the simulated design library, in memory, so that what a sheet saves,
// duplicates or deletes shows in the gallery. To be replaced by /admin/templates (phase 1 back).
const useAdminTemplatesStore = defineStore('adminTemplates', () => {
    const templates = ref<IAdminTemplate[]>(structuredClone(ADMIN_TEMPLATES));
    // ADM-03: the themes and sub-themes the designs are filed under, administrable.
    const themes = ref<IAdminTemplateTheme[]>(structuredClone(ADMIN_TEMPLATE_THEMES));

    // `renamed` maps, per theme, a sub-theme's former French label to its new one: the designs
    // filed under it follow.
    function saveThemes(next: IAdminCategory[], renamed: Record<string, Record<string, string>> = {}): void {
        themes.value = next.map((theme) => ({
            id: theme.id,
            label: theme.label,
            translations: theme.translations,
            subCategories: (theme.subCategories ?? []).filter(Boolean),
            subCategoryTranslations: theme.subCategoryTranslations,
        }));
        templates.value.forEach((template) => {
            const nextLabel = renamed[template.themeId]?.[template.subCategory];
            if (nextLabel) {
                template.subCategory = nextLabel;
            }
        });
    }

    function themeLabel(id: string): string {
        return themes.value.find((theme) => theme.id === id)?.label ?? '';
    }

    function today(): string {
        return new Date().toISOString().slice(0, 10);
    }

    function getTemplate(id: string): IAdminTemplate | undefined {
        return templates.value.find((template) => template.id === id);
    }

    // A detached copy for a sheet to edit. structuredClone cannot read Vue's proxies, hence toRaw.
    function getTemplateCopy(id: string): IAdminTemplate | undefined {
        const template = getTemplate(id);
        return template && structuredClone(toRaw(template));
    }

    function saveTemplate(updated: IAdminTemplate): void {
        const index = templates.value.findIndex((template) => template.id === updated.id);
        if (index === -1) {
            console.error(`Admin templates: no template ${updated.id} to save`);
            return;
        }
        templates.value[index] = { ...structuredClone(toRaw(updated)), updatedAt: today() };
    }

    function addTemplate(template: IAdminTemplate): void {
        templates.value.unshift({ ...template, updatedAt: today() });
    }

    // A duplicate starts as a draft, right after its original, so it is found where one looks.
    function duplicateTemplate(id: string): IAdminTemplate | undefined {
        const index = templates.value.findIndex((template) => template.id === id);
        const original = templates.value[index];
        if (!original) {
            console.error(`Admin templates: no template ${id} to duplicate`);
            return undefined;
        }
        const copy: IAdminTemplate = {
            ...structuredClone(toRaw(original)),
            id: `${id}-copie-${Date.now()}`,
            isActive: false,
            name: { ...original.name, fr: `${original.name.fr} (copie)` },
            updatedAt: today(),
        };
        templates.value.splice(index + 1, 0, copy);
        return copy;
    }

    // What the editor hands back when a design is saved: its elements, locked or not in the
    // editor's layers, where the locks are set (ADM-03), and the thumbnail made from it.
    function recordDrawing(id: string, drawing: { fields: IAdminTemplateField[]; thumbnailUrl?: string }): void {
        const template = templates.value.find((candidate) => candidate.id === id);
        if (!template) {
            console.error(`Admin templates: no template ${id} to record a drawing on`);
            return;
        }
        template.fields = drawing.fields;
        template.thumbnailUrl = drawing.thumbnailUrl ?? template.thumbnailUrl;
        template.updatedAt = today();
    }

    function deleteTemplate(id: string): void {
        templates.value = templates.value.filter((template) => template.id !== id);
    }

    function setTemplateActive(id: string, isActive: boolean): void {
        const template = getTemplate(id);
        if (!template) {
            console.error(`Admin templates: no template ${id} to toggle`);
            return;
        }
        template.isActive = isActive;
    }

    return {
        addTemplate,
        deleteTemplate,
        duplicateTemplate,
        getTemplate,
        getTemplateCopy,
        recordDrawing,
        saveTemplate,
        saveThemes,
        setTemplateActive,
        templates,
        themeLabel,
        themes,
    };
});

export { useAdminTemplatesStore };
